import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { BUTTON_SIZES, Button } from 'ui/button';
import { Link } from 'ui/links';
import { AddNew16 } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { IndexTabsList, TabsGroup } from 'ui/tabsGroup';
import { PROJECT_PERMISSIONS } from 'utils/permissions';

import { PLACEMENT_DISPLAY_CONTENT } from 'modules/placements/constants';
import { DisplayPriorityProvider } from 'modules/placements/context/DisplayPriorityProvider';
import { usePlacementsManageContext } from 'modules/placements/context/PlacementsManageContext';
import { PlacementsManageContextProvider } from 'modules/placements/context/PlacementsManageContextProvider';
import { DisplayPriorityTakeover } from 'modules/placements/takeovers';
import { PlacementStatus } from 'modules/placements/types';
import {
  MainBody,
  ManagePlacementsContainer,
  PageActions,
  PlacementsHeader,
  Title,
} from '../styles';
import { PlacementDetails } from './PlacementDetails';
import { PlacementsArchiveModal } from './PlacementsArchiveModal';
import { PlacementsEmptyState } from './PlacementsEmptyState';
import { PlacementsList } from './PlacementsList';
import { PlacementsMissingPermissionState } from './PlacementsMissingPermissionState';
import { PlacementStatusTab, PlacementsSubHeader, StyledSearch } from './styles';

const { managePlacementsDocsLabel, placementsDocumentationPath } = PLACEMENT_DISPLAY_CONTENT;

const withContext = (Component: FC) => () => {
  return (
    <PlacementsManageContextProvider>
      <Component />
    </PlacementsManageContextProvider>
  );
};

export const PlacementsManage = withContext(() => {
  const canViewPlacements = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ViewChannelConfiguration,
  );
  const canEditPlacements = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ManageChannelConfiguration,
  );

  const {
    placements,
    placementsLoading,
    selectedPlacement,
    searchText,
    setStatusFilterTab,
    setSearchText,
    isArchiveModalOpen,
    isDisplayPriorityTakeoverOpen,
    setIsDisplayPriorityTakeoverOpen,
  } = usePlacementsManageContext();

  useEffect(() => {
    document.title = 'Embedded Placements';
  });

  const history = useHistory();

  return (
    <DisplayPriorityProvider>
      <ManagePlacementsContainer data-test="placements-manage">
        {!canViewPlacements ? (
          <PlacementsMissingPermissionState />
        ) : (
          <>
            <PlacementsHeader>
              <Title>Embedded Placements</Title>
              <PageActions>
                <Link
                  data-test="placements-documentation-link"
                  href={placementsDocumentationPath}
                  target="_blank"
                  hasOpenNewTabIcon
                >
                  {managePlacementsDocsLabel}
                </Link>
                <Button
                  onClick={() => history.push('/settings/placements/create')}
                  data-test="placements-add-button"
                  isDisabled={!canEditPlacements}
                  size={BUTTON_SIZES.LARGE}
                >
                  <AddNew16 />
                  New placement
                </Button>
              </PageActions>
            </PlacementsHeader>
            <PlacementsSubHeader isEmptyState={!placements?.length}>
              <TabsGroup defaultValue={PlacementStatus.ACTIVE}>
                <IndexTabsList>
                  <PlacementStatusTab
                    data-test="placement-status-active"
                    onClick={() => setStatusFilterTab(PlacementStatus.ACTIVE)}
                    value={PlacementStatus.ACTIVE}
                  >
                    Active
                  </PlacementStatusTab>
                  <PlacementStatusTab
                    data-test="placement-status-archived"
                    onClick={() => setStatusFilterTab(PlacementStatus.ARCHIVED)}
                    value={PlacementStatus.ARCHIVED}
                  >
                    Archived
                  </PlacementStatusTab>
                </IndexTabsList>
              </TabsGroup>
              <StyledSearch
                data-test="placements-search"
                id="placements-search"
                onChange={(search) => setSearchText(search)}
                placeholder="Search placements"
                value={searchText}
              />
            </PlacementsSubHeader>
            <MainBody>
              {placementsLoading ? (
                <Spinner />
              ) : (
                <>
                  {placements?.length ? (
                    <>
                      {isDisplayPriorityTakeoverOpen && (
                        <DisplayPriorityTakeover
                          onClose={() => setIsDisplayPriorityTakeoverOpen(false)}
                        />
                      )}
                      <PlacementsList placements={placements} />
                      {selectedPlacement && <PlacementDetails placement={selectedPlacement} />}
                    </>
                  ) : (
                    <PlacementsEmptyState />
                  )}
                </>
              )}
            </MainBody>
            {isArchiveModalOpen && <PlacementsArchiveModal />}
          </>
        )}
      </ManagePlacementsContainer>
    </DisplayPriorityProvider>
  );
});
